import React, { useCallback } from 'react'
import { Radio } from '@roo/roo'
import { FEFormData, RulePackageRes } from '../utils/types'
import { useTranslation } from '@sailor/i18n-web'
import Selector from '@roo/roo/Selector'
import { useRequest } from 'ahooks'
import { fetchBrandList, fetchShopList } from '../utils/api'
import debounce from 'lodash-es/debounce'

interface ShopLimitSelectorProps {
  value: FEFormData['shopLimitConfig'];
  onChange: (value: ShopLimitSelectorProps['value']) => void;
}

enum LimitConfigEnum {
  NO_LIMIT = 'NO_LIMIT',
  LIMIT = 'LIMIT',
}

const ShopLimitSelector: React.FC<ShopLimitSelectorProps> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const { data: shopListRes, run: getShopList, loading: shopListLoading } = useRequest((shopName) => fetchShopList({ pageNo: 1, pageSize: 100, shopName }), { manual: true })
  const { data: brandListRes, run: getBrandList, loading: brandListLoading } = useRequest((brandName) => fetchBrandList({ pageNo: 1, pageSize: 100, brandName }), { manual: true })

  const debouncedGetShopList = useCallback(debounce(getShopList, 300), [getShopList])
  const debouncedGetBrandList = useCallback(debounce(getBrandList, 300), [getBrandList])

  const handleAssignAllChange = (newType: LimitConfigEnum) => {
    onChange({ ...value, enable: newType === LimitConfigEnum.LIMIT })
  }


  const handleShopChange = (newOptions:  FEFormData['shopLimitConfig']['shopList']) => {
    onChange({ ...value, shopList: newOptions })
  }

  const handleDepartmentChange = (newOptions:  FEFormData['shopLimitConfig']['brandList']) => {
    onChange({ ...value, brandList: newOptions })
  }

  return (
    <div>
      <Radio.Group value={value.enable ? LimitConfigEnum.LIMIT : LimitConfigEnum.NO_LIMIT} onChange={handleAssignAllChange}>
        <Radio value={'NO_LIMIT'}>{t('e_pc_no_limit', '不限制')}</Radio>
        <Radio value="LIMIT">{t('e_pc_custom', '自定义')}</Radio>
      </Radio.Group>
      {value.enable && (
        <div style={{ paddingTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ lineHeight: '45px' }}>{t('e_pc_brand', '品牌')}&ensp;</label>
            <Selector multiple filterable  clearable
              onSearch={debouncedGetBrandList}
              style={{ flex: 1 }}
              loading={brandListLoading}
              value={value.brandList}
              options={generateBrandOption(brandListRes?.brandList)}
              onChange={handleDepartmentChange}
              notFoundContent={<EmptyContent/>}
              placeholder={t('e_pc_brand_search_select')}
              rawOption // NOTE: 依赖该属性选中整个option,否则回填会有问题
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center',  paddingTop: '16px' }}>
            <label style={{ lineHeight: '45px' }}>{t('e_pc_store', '店铺')}&ensp;</label>
            <Selector multiple  filterable  clearable
              onSearch={debouncedGetShopList}
              style={{ flex: 1 }}
              loading={shopListLoading}
              value={value.shopList}
              onChange={handleShopChange}
              options={generateShopOption(shopListRes?.shopList)}
              placeholder={t('e_pc_store_search_select')}
              rawOption // NOTE: 依赖该属性选中整个option,否则回填会有问题
              notFoundContent={<EmptyContent/>}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyContent () {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <img src="https://img-ap-hongkong.mykeeta.net/sailorcweb/2c2b81059363030b9731b068d82b3acc14700.png" alt="" style={{ width: 80, height: 80 }}/>
      <div style={{ padding: '4px 0' }}>
        {t('e_pc_search_no_data', 'No data')}
      </div>
    </div>)
}

function generateShopOption (shopList: {
  shopId: string;
  shopName: string;
  shopAddress: string;
}[]) {
  const getAddressInfo = (item) => (item.shopAddress ? `(${item.shopAddress})` : '')
  return shopList?.map(item => ({ label: `${item.shopName}${getAddressInfo(item)}`, value: item.shopId, originValue: item })) || []
}

function generateBrandOption (brandList?:  {
  brandId: string;
  brandName: string;
}[]) {
  return brandList?.map(item => ({ label: item.brandName, value: item.brandId, originValue: item })) || []
}

export function generateFormShopLimitConfig (resData?: RulePackageRes) {
  const shopEnable = resData?.bannedShopRule?.checkBannedShop
  const branEnable = resData?.bannedBrandRule?.checkBannedBrand
  return {
    enable: shopEnable || branEnable,
    shopList: generateShopOption(resData?.bannedShopRule?.bannedShopList),
    brandList: generateBrandOption(resData?.bannedBrandRule?.bannedBrandList),
  }

}


export default ShopLimitSelector
